import type {
  WebhookInstallStatus,
  WebhookProviderCatalogEntry,
} from '../types';

const WEBHOOK_SETUP_KINDS_REQUIRING_TOOL_CONNECTION = new Set<
  WebhookProviderCatalogEntry['setup']['kind']
>(['app_level_then_user_oauth', 'api_event_subscription', 'set_webhook_url']);

const TELEGRAM_BOT_WEBHOOK_TOOL_KEY = 'telegram/messages/send-message';

export function requiresWebhookProviderToolConnection(
  catalog: WebhookProviderCatalogEntry
): boolean {
  if (catalog.key === 'generic') {
    return false;
  }
  // Providers that collect credentials directly on the webhook page
  // (provider_account) use WebhookProviderCredentialSection, not the tool
  // connection flow — don't render both.
  if (catalog.credentials?.kind === 'provider_account') {
    return false;
  }
  return WEBHOOK_SETUP_KINDS_REQUIRING_TOOL_CONNECTION.has(catalog.setup.kind);
}

export function resolveWebhookToolProviderKey(
  catalog: WebhookProviderCatalogEntry
): string | null {
  if (!requiresWebhookProviderToolConnection(catalog)) {
    return null;
  }
  return catalog.key;
}

export function webhookProviderConnectionDescription(
  catalog: WebhookProviderCatalogEntry
): string {
  if (catalog.key === 'telegram') {
    return 'Connect a Telegram bot token before installing webhooks. User session credentials cannot receive bot webhooks.';
  }
  if (catalog.setup.kind === 'app_level_then_user_oauth') {
    return `Connect ${catalog.displayName} before installing webhook subscriptions.`;
  }
  if (catalog.setup.kind === 'api_event_subscription') {
    return `Connect ${catalog.displayName} so Cotera can install webhook subscriptions via API.`;
  }
  return `Connect ${catalog.displayName} so Cotera can register the webhook URL.`;
}

export function isWebhookProviderToolConnectionConfigured(params: {
  catalog: WebhookProviderCatalogEntry;
  provider: {
    configStatus: 'configured' | 'unconfigured';
    tools: ReadonlyArray<{ key: string; canExecute: boolean }>;
  } | null;
}): boolean {
  if (params.provider === null) {
    return false;
  }
  if (params.catalog.key === 'telegram') {
    const sendMessageTool = params.provider.tools.find(
      (tool) => tool.key === TELEGRAM_BOT_WEBHOOK_TOOL_KEY
    );
    return sendMessageTool?.canExecute === true;
  }
  return params.provider.configStatus === 'configured';
}

export function shouldSuppressWebhookInstallError(params: {
  catalog: WebhookProviderCatalogEntry;
  installStatus?: WebhookInstallStatus;
  provider: {
    configStatus: 'configured' | 'unconfigured';
    tools: ReadonlyArray<{ key: string; canExecute: boolean }>;
  } | null;
}): boolean {
  return (
    params.installStatus === 'error' &&
    requiresWebhookProviderToolConnection(params.catalog) &&
    !isWebhookProviderToolConnectionConfigured({
      catalog: params.catalog,
      provider: params.provider,
    })
  );
}
